import { ChartCanvas, Chart } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { BarSeries, LineSeries } from "@react-financial-charts/series";
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } from "@react-financial-charts/coordinates";
import { format } from "d3-format";
import type { FlowsPointDto } from "../api";
import {
  CHART_HEIGHT, FII_COLOR, DII_COLOR, VIX_UP_COLOR, VIX_DOWN_COLOR,
  useContainerWidth, buildScale, symmetricExtent, NotEnoughPoints,
} from "./flowsChartShared";

/** TAB 1 "Daily flows" (default) -- react-financial-charts, same library and
 * conventions as PriceChart.tsx (Gate-0's mandated production charting
 * library -- no hand-rolled SVG).
 *
 * Answers a specific question: "when VIX (fear) moves, who buys -- FII or
 * DII?" Everything on this chart is 0-centered:
 *  - FII net / DII net (real INR crore) as LINES, already 0-centered
 *    (positive = net buying, negative = net selling) -- unchanged.
 *  - India VIX DAY-OVER-DAY CHANGE (not the level -- see 2026-09-10) as
 *    BARS on a genuinely independent second y-axis, also 0-centered: a bar
 *    above 0 means fear rose that day, below means fear fell.
 *
 * FII/DII stay in raw ₹cr, never %-change -- they cross zero often enough
 * (~34% of sampled sessions) that %-change is undefined/explosive there
 * (see app/api/routes/market.py's FlowsPointOut docstring for a concrete
 * blown-up example). VIX is always strictly positive, so ITS change is a
 * clean, well-defined number -- that asymmetry is why only VIX takes this
 * transform. The correlation readout rendered alongside this chart
 * (MarketPage.tsx) is the quantified answer, computed server-side over the
 * same gap-safe pairing.
 *
 * The discontinuous time scale plots only the dates actually present -- a
 * genuine gap (the source's own documented day<=9/month<=9 exclusion, see
 * that module's docstring) simply isn't a point here, never interpolated
 * or faked. The caller is expected to show `flows_series_note` alongside
 * this chart so a viewer knows WHY consecutive points aren't always
 * consecutive trading days, since the chart itself can't say that on its
 * own -- gaps are silent by construction on any discontinuous scale.
 */

interface ChartDatum {
  date: Date;
  fii_net: number | null;
  dii_net: number | null;
  vix_change: number | null;
}

export function FlowsChart({ points }: { points: FlowsPointDto[] }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const data: ChartDatum[] = points.map((p) => ({
    date: new Date(p.date), fii_net: p.fii_net, dii_net: p.dii_net, vix_change: p.vix_change,
  }));

  if (data.length < 2 || width === 0) {
    return data.length < 2 ? <NotEnoughPoints containerRef={ref} /> : <div ref={ref} style={{ minHeight: 40 }} />;
  }

  const { plotData, xScale, xAccessor, displayXAccessor, xExtents, formatTickDate } = buildScale(data);

  const vixChangeValues = data.map((d) => d.vix_change);
  const hasVix = vixChangeValues.some((v) => v != null);
  const vixExtents = symmetricExtent(vixChangeValues);

  return (
    <div ref={ref}>
      <div className="flows-legend">
        <span><i style={{ background: FII_COLOR }} /> FII net (₹cr)</span>
        <span><i style={{ background: DII_COLOR }} /> DII net (₹cr)</span>
        {hasVix ? (
          <span>
            <i style={{ background: VIX_UP_COLOR }} />/<i style={{ background: VIX_DOWN_COLOR }} /> VIX day-change
            (right axis, fear ↑/↓)
          </span>
        ) : null}
      </div>
      <ChartCanvas
        height={CHART_HEIGHT}
        width={width}
        ratio={window.devicePixelRatio || 1}
        margin={{ left: 56, right: hasVix ? 48 : 8, top: 8, bottom: 24 }}
        data={plotData}
        seriesName="flows"
        xScale={xScale}
        xAccessor={xAccessor}
        displayXAccessor={displayXAccessor}
        xExtents={xExtents}
      >
        <Chart
          id={0}
          height={CHART_HEIGHT}
          yExtents={(d: ChartDatum) => [d.fii_net ?? 0, d.dii_net ?? 0]}
          padding={{ top: 10, bottom: 10 }}
        >
          <XAxis showGridLines tickFormat={formatTickDate("%d %b")} />
          <YAxis showGridLines tickFormat={format(".2s")} />
          <LineSeries yAccessor={(d: ChartDatum) => d.fii_net ?? undefined} strokeStyle={FII_COLOR} strokeWidth={1.4} />
          <LineSeries yAccessor={(d: ChartDatum) => d.dii_net ?? undefined} strokeStyle={DII_COLOR} strokeWidth={1.4} />
          <MouseCoordinateY displayFormat={format(".2f")} />
          <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />
        </Chart>
        {hasVix ? (
          // Overlaid on the SAME screen position as Chart id=0 (default
          // origin [0,0], same height) -- a genuine second axis, not a
          // second panel. Only this chart's own yExtents/scale governs
          // the VIX bars; it shares the parent xScale/xAccessor but never
          // touches FII/DII's y-domain. baseAt is forced to yScale(0) --
          // BarSeries' own default bases bars at the canvas edge, which
          // would draw every bar from the top of the panel down rather
          // than growing from a shared 0-line.
          <Chart id={1} height={CHART_HEIGHT} yExtents={vixExtents} padding={{ top: 10, bottom: 10 }}>
            <YAxis axisAt="right" orient="right" showGridLines={false} tickFormat={format("+.1f")} />
            <BarSeries
              yAccessor={(d: ChartDatum) => d.vix_change ?? undefined}
              baseAt={(_xScale, yScale) => yScale(0)}
              fillStyle={(d: ChartDatum) => (d.vix_change != null && d.vix_change >= 0 ? VIX_UP_COLOR : VIX_DOWN_COLOR)}
              widthRatio={0.4}
            />
          </Chart>
        ) : null}
        <CrossHairCursor />
      </ChartCanvas>
    </div>
  );
}
